import React, { useState, useEffect } from 'react';
import Button from './Button';
import { X, Download, Smartphone, Apple, ArrowRight, Share } from 'lucide-react';

/**
 * PWA Installation Prompt Component
 *
 * A modern, platform-specific prompt to encourage users to install the PWA
 * Detects iOS/Safari and Android/Chrome to show appropriate installation instructions
 *
 * @param {Object} props Component props
 * @param {Function} props.onInstall Function to call when user clicks install (Android/Chrome)
 * @param {Function} props.onDismiss Function to call when user dismisses the prompt
 */
const PWAInstallPrompt = ({ onInstall, onDismiss }) => {
  const [isIOS, setIsIOS] = useState(false);
  const [isStep2, setIsStep2] = useState(false);

  useEffect(() => {
    // Detect iOS devices
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
  }, []);

  const showIOSInstructions = () => {
    setIsStep2(true);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
      <div className="max-w-md mx-auto m-4">
        <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl shadow-xl p-5 border border-gray-200 dark:border-gray-700">
          {/* Header with icon + close button */}
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 mr-4 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl p-3 text-white shadow-sm">
              {isIOS ? <Apple size={24} strokeWidth={2} /> : <Smartphone size={24} strokeWidth={2} />}
            </div>
            <div className="flex-1">
              <h3 className="font-display text-xl font-bold text-gray-900 dark:text-white mb-1">
                {isIOS ? "Add to Home Screen" : "Install TourneyPro"}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isIOS 
                  ? "Install our app for the best experience on your iOS device" 
                  : "Get offline access and a full-screen experience"}
              </p>
            </div>
            <button
              onClick={onDismiss}
              className="ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss"
            >
              <X size={20} />
            </button>
          </div>

          {/* iOS-specific instructions */}
          {isIOS && (
            <div className="mb-4">
              {!isStep2 ? (
                <div className="flex flex-col">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-3">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Step 1:</p>
                    <div className="flex items-center">
                      <Share size={18} className="mr-2 text-primary-500" />
                      <p className="text-sm">Tap the share icon in your browser</p>
                    </div>
                  </div>
                  <Button
                    primary
                    rounded
                    className="w-full justify-center"
                    onClick={showIOSInstructions}
                  >
                    Next <ArrowRight size={16} className="ml-1" />
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col">
                  <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-3">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">Step 2:</p>
                    <div className="flex items-center">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900 dark:text-primary-300 mr-2">+</span>
                      <p className="text-sm">Select "Add to Home Screen"</p>
                    </div>
                  </div>
                  <Button
                    secondary
                    rounded
                    className="w-full justify-center"
                    onClick={onDismiss}
                  >
                    Got it
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Android/Chrome buttons */}
          {!isIOS && (
            <div className="flex flex-col space-y-3">
              <Button
                primary
                rounded
                icon={<Download size={18} />}
                className="w-full justify-center"
                onClick={onInstall}
              >
                Install Now
              </Button>
              <Button
                secondary
                rounded
                className="w-full justify-center"
                onClick={onDismiss}
              >
                Maybe Later
              </Button>
            </div>
          )}

          {/* Footer info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Fast access • Offline support • No app store needed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
